import React from "react";
import { 
  BarChart3, 
  Target, 
  Timer, 
  Dumbbell, 
  Save, 
  Filter, 
  Trophy,
  CheckCircle2,
  TrendingUp,
  Zap
} from "lucide-react";

// ==========================================
// --- MINI MOCKUP COMPONENTS ---
// ==========================================

const ScoreCardMockup = ({ title, type, status, target, score }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm w-full max-w-xs relative overflow-hidden">
    <div className="flex justify-between items-start mb-4">
      <h4 className="font-bold text-gray-900 text-sm">{title}</h4>
      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[8px] font-black uppercase tracking-wider ${
        type === 'timed' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-blue-50 border-blue-200 text-blue-700'
      }`}>
        {type === 'timed' ? <Timer size={10} /> : <Zap size={10} />}
        {type === 'timed' ? 'Timed' : 'Height/Reps'}
      </div>
    </div>
    <div className="bg-gray-50 p-3 rounded-lg flex justify-between items-center border border-gray-100">
      <div>
        <p className="text-[8px] font-bold text-gray-400 uppercase">Target</p>
        <p className="text-lg font-black text-gray-800">{target}</p>
      </div>
      <div className="text-right">
        <p className="text-[8px] font-bold text-gray-400 uppercase">Your Score</p>
        <div className={`text-sm font-bold px-3 py-1 rounded-md border-2 transition-colors ${
          status === 'success' ? 'bg-green-50 border-green-500 text-green-700' : 'bg-white border-red-300 text-red-600'
        }`}>
          {score}
        </div>
      </div>
    </div>
  </div>
);

// ==========================================
// --- MAIN PAGE COMPONENT ---
// ==========================================

const ScoresPage = () => {
  return (
    <div className="space-y-12 pb-20">
      {/* Header Section */}
      <section>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-100 mb-4">
          <BarChart3 size={14} className="text-orange-600" />
          <span className="text-[10px] font-black text-orange-600 uppercase tracking-widest">Athlete Metrics</span>
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Performance Tracking</h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          Your scores are the pulse of your athletic profile. By entering your <strong>Combine</strong> and <strong>Skill</strong> data, 
          the Nuvio engine calculates your growth and matches you against elite standards.
        </p>
      </section>

      {/* 1. Score Validation Logic */}
      <section className="grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <div className="bg-green-100 p-2 rounded-lg text-green-600"><TrendingUp size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">Understanding Success</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            The dashboard gives you instant feedback. A <span className="text-green-600 font-bold">Green highlight</span> means you've 
            hit or exceeded the target. A <span className="text-red-500 font-bold">Red highlight</span> shows where you have room to grow.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <Dumbbell className="text-blue-500 mt-1" size={20} />
                <div>
                    <p className="text-sm font-bold text-gray-900">Strength & Explosiveness</p>
                    <p className="text-xs text-gray-500 italic">For drills like Vertical Jump or Bench Press, <strong>higher</strong> scores are the goal.</p>
                </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <Timer className="text-amber-500 mt-1" size={20} />
                <div>
                    <p className="text-sm font-bold text-gray-900">Speed & Agility</p>
                    <p className="text-xs text-gray-500 italic">For timed drills like the 40-Yard Dash, <strong>lower</strong> times mean better performance.</p>
                </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 items-center justify-center bg-gray-50 p-8 rounded-3xl border border-gray-200 border-dashed">
            <ScoreCardMockup title="Vertical Jump" type="standard" status="success" target="28.5" score="31.2" />
            <ScoreCardMockup title="40-Yard Dash" type="timed" status="fail" target="4.50" score="4.82" />
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* 2. Personalized for Your Position */}
      <section className="grid md:grid-cols-2 gap-12">
        <div className="order-2 md:order-1 flex flex-col justify-center">
             <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm relative">
                <div className="absolute top-4 right-4 animate-pulse">
                    <CheckCircle2 className="text-blue-500" size={24} />
                </div>
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6 text-center">Your Smart Filter</h4>
                <div className="space-y-3">
                    <div className="py-3 px-4 bg-blue-600 text-white rounded-xl text-sm font-bold text-center">Football Player</div>
                    <div className="py-3 px-4 bg-blue-400 text-white rounded-xl text-sm font-bold text-center">Wide Receiver</div>
                    <div className="py-4 px-4 bg-gray-50 border-2 border-dashed border-blue-200 rounded-xl text-xs font-medium text-blue-600 text-center italic">
                        "Only showing WR specific speed and catching drills"
                    </div>
                </div>
             </div>
        </div>
        <div className="order-1 md:order-2 space-y-5">
          <div className="flex items-center gap-2">
            <div className="bg-orange-100 p-2 rounded-lg text-orange-600"><Filter size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">Drills That Matter</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            You don't need to scroll through drills that don't apply to you. Nuvio automatically detects your <strong>Sport</strong> and <strong>Position</strong> 
            to show you the exact benchmarks scouts look for in your specific role.
          </p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle2 size={16} className="text-green-500"/> Skill drills curated for your position</li>
            <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle2 size={16} className="text-green-500"/> Benchmarks set by your specific Program</li>
          </ul>
        </div>
      </section>

      {/* 3. Saving & Progress */}
      <section className="bg-slate-900 rounded-3xl p-8 text-white">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-2">
              <Save className="text-green-400" />
              <h3 className="text-2xl font-bold">Lock in Your Progress</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              When you save your scores, the system updates your "Personal Best" history. This creates a timeline of your 
              improvement that coaches can follow throughout the season. 
            </p>
          </div>
          <div className="w-full md:w-64 bg-slate-800 p-6 rounded-2xl border border-slate-700 text-center">
                <Trophy className="mx-auto text-yellow-400 mb-2" size={32} />
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">Profile Readiness</p>
                <div className="mt-2 h-2 w-full bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 w-[75%]"></div>
                </div>
                <p className="mt-2 text-[10px] font-bold text-green-400">75% Complete</p>
          </div>
        </div>
      </section>

      {/* Summary Footer */}
      <section className="text-center pt-8 border-t border-gray-100">
        <h4 className="text-sm font-bold text-gray-900 mb-2">Ready to show out?</h4>
        <p className="text-xs text-gray-500 max-w-lg mx-auto italic">
            "Your scores are your resume. Keep them updated to stay at the top of the leaderboard 
            and keep the AI training engine tuned to your needs."
        </p>
      </section>
    </div>
  );
};

export default ScoresPage;